/**
 * MobileNavSheet (iter247)
 *
 * The left sidebar rail is `hidden md:flex`, so on phones every sidebar
 * destination used to be unreachable except by typing the URL. This bottom
 * sheet mirrors the SAME ordered `items` array the desktop rail renders
 * (including the user's custom drag order and live badges), so mobile and
 * desktop can never drift apart.
 *
 * Opened from the "More" button in the mobile bottom bar.
 */
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  NavContextMenu, OpenInNewTabButton, isModifiedClick,
} from '@/components/common/NewTabAffordance';

const MobileNavSheet = ({ open, onOpenChange, items, activePath, onNavigate }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent
      side="bottom"
      className="md:hidden bg-[#0F1730] border-t border-[#1F2A4D] max-h-[80vh] overflow-y-auto p-4"
      data-testid="mobile-nav-sheet"
    >
      <SheetHeader className="mb-3 text-left">
        <SheetTitle className="text-slate-100 text-base">All destinations</SheetTitle>
      </SheetHeader>
      <div className="grid grid-cols-3 gap-2 pb-2">
        {items.map((item) => {
          const Icon = item.icon;
          const slug = item.testId || item.label.toLowerCase().replace(/\s+/g, '-');
          const active = activePath === item.path;
          // iter248 — real anchor, so a long-press offers the browser's own
          // "open in new tab" and a modified click behaves like any link.
          const handleClick = (e) => {
            if (isModifiedClick(e)) return;
            e.preventDefault();
            onNavigate(item.path);
            onOpenChange(false);
          };
          return (
            <div key={item.path} className="relative">
              <NavContextMenu path={item.path} label={item.label} slug={`sheet-${slug}`}>
                <a
                  href={item.path}
                  onClick={handleClick}
                  className={`relative flex flex-col items-center justify-center gap-1.5 rounded-lg border p-3 no-underline transition-colors ${
                    active
                      ? 'border-[#DC2626] bg-[#DC2626]/15 text-white'
                      : 'border-[#1F2A4D] bg-[#0A1024] text-slate-300 hover:bg-[#131C3A]'
                  }`}
                  data-testid={`mobile-nav-sheet-${slug}`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-[11px] leading-tight text-center">{item.label}</span>
                </a>
              </NavContextMenu>
              {/* Always visible here — touch has no hover state. Top-LEFT so
                  it never collides with the unread badge at top-right. */}
              <OpenInNewTabButton
                path={item.path}
                label={item.label}
                slug={`sheet-${slug}`}
                className="absolute top-1 left-1 h-5 w-5 text-slate-400 hover:text-white"
              />
              {item.badge > 0 && (
                <span
                  className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-semibold flex items-center justify-center pointer-events-none"
                  data-testid={`mobile-nav-sheet-badge-${slug}`}
                >
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </SheetContent>
  </Sheet>
);

export default MobileNavSheet;
